/**
 * Airframe health, and the calibrations that fix it.
 *
 * Reads are view-gated and unlogged: a diagnostics panel polls, and a poll that
 * writes an activity row per tick buries the orders an operator actually gave.
 * Everything that changes the aircraft is labelled and logged.
 *
 * The one shape worth knowing before using this: a calibration is a
 * conversation, not a call. `startCalibration` returns as soon as the aircraft
 * accepts the command; what it then wants — "place vehicle level and press
 * any key" — arrives through `calibrationStatus`, whose `state` can be
 * `waiting_for_position`. That is the state a spinner cannot express, and the
 * reason the panel polls rather than awaiting a result.
 */

import { api, type ApiResult } from '@/api/client';
import type { CommandResult, DroneId } from '@/types/domain';

/** OK / WARN / FAIL / UNKNOWN, lowercase on the wire. UNKNOWN is a real
 * answer — the aircraft never reported the thing — and is never rendered as
 * healthy. */
export type DiagnosticLevel = 'ok' | 'warn' | 'fail' | 'unknown';

export interface DiagnosticFinding {
  key: string;
  subject: string;
  level: DiagnosticLevel;
  detail: string;
}

export interface DiagnosticSection {
  name: string;
  level: DiagnosticLevel;
  findings: DiagnosticFinding[];
}

export interface DiagnosticsReport {
  droneId: string;
  /** The worst level across every section. */
  level: DiagnosticLevel;
  /** The one line to read first: the worst finding, named. */
  summary: string;
  sections: DiagnosticSection[];
}

export type CalibrationKindName =
  | 'gyro'
  | 'accel_full'
  | 'accel_simple'
  | 'level'
  | 'compass'
  | 'barometer'
  | 'radio'
  | 'esc';

export interface CalibrationKindInfo {
  kind: CalibrationKindName;
  label: string;
  /** What the operator has to physically do first. Served by the server rather
   * than duplicated here, so the preconditions an operator reads cannot drift
   * from the ones the server holds. */
  precondition: string;
  /** Needs the operator to move the airframe and confirm each position. */
  interactive: boolean;
  cancellable: boolean;
}

export type CalibrationState = 'running' | 'waiting_for_position' | 'succeeded' | 'failed';

export interface CalibrationStatus {
  droneId: string;
  active: boolean;
  startedAt?: number;
  kind?: CalibrationKindName;
  state?: CalibrationState;
  /** What to do now, in the autopilot's words. Null when there is nothing to do. */
  instruction?: string | null;
  /** The position step the aircraft is waiting on. An acknowledgement has to
   * echo this exact number back. */
  position?: number | null;
  compassPercent?: Record<string, number>;
  /** Everything the autopilot has said since this calibration started, newest
   * first, verbatim. Shown unedited — the classifier only infers terminal
   * states and never rewrites a line. */
  lines?: string[];
}

export function getDiagnostics(id: DroneId): Promise<ApiResult<DiagnosticsReport>> {
  return api.get<DiagnosticsReport>(`/diagnostics/${encodeURIComponent(id)}`, { logged: false });
}

export function getCalibrationKinds(
  id: DroneId,
): Promise<ApiResult<{ droneId: string; kinds: CalibrationKindInfo[] }>> {
  return api.get<{ droneId: string; kinds: CalibrationKindInfo[] }>(
    `/diagnostics/${encodeURIComponent(id)}/calibration/kinds`,
    { logged: false },
  );
}

export function getCalibrationStatus(id: DroneId): Promise<ApiResult<CalibrationStatus>> {
  return api.get<CalibrationStatus>(`/diagnostics/${encodeURIComponent(id)}/calibration`, {
    logged: false,
  });
}

export function startCalibration(
  id: DroneId,
  kind: CalibrationKindName,
): Promise<ApiResult<{ droneId: string; kind: string; result: CommandResult }>> {
  return api.post<{ droneId: string; kind: string; result: CommandResult }>(
    `/diagnostics/${encodeURIComponent(id)}/calibration`,
    { kind },
    { label: `CALIBRATE ${kind.toUpperCase()} ${id}` },
  );
}

/**
 * Confirm the airframe is in the position the aircraft asked for.
 *
 * Takes the position rather than assuming the next one, because the number has
 * to echo what the aircraft is actually waiting on — ArduPilot does not
 * necessarily walk the six positions in order, and acknowledging a step it is
 * not on is silently ignored.
 */
export function acknowledgePosition(
  id: DroneId,
  position: number,
): Promise<ApiResult<{ droneId: string; position: number; result: CommandResult }>> {
  return api.post<{ droneId: string; position: number; result: CommandResult }>(
    `/diagnostics/${encodeURIComponent(id)}/calibration/position`,
    { position },
    { label: `CAL POSITION ${position} ${id}` },
  );
}

/** Stop the running calibration. 404 when none is running. */
export function cancelCalibration(
  id: DroneId,
): Promise<ApiResult<{ droneId: string; kind: string; result: CommandResult }>> {
  return api.post<{ droneId: string; kind: string; result: CommandResult }>(
    `/diagnostics/${encodeURIComponent(id)}/calibration/cancel`,
    {},
    { label: `CAL CANCEL ${id}` },
  );
}
